import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  Share2, Copy, Check, X, MessageCircle, Mail, Send,
  Link as LinkIcon
} from "lucide-react";
import { SOCIAL, withShareCacheBust } from "../../lib/social";

/**
 * Reusable social-share popover.
 *
 * Strategy:
 *  1. On supporting browsers (most mobiles), tapping the trigger calls
 *     `navigator.share()` which opens the native OS share sheet — Instagram,
 *     Messages, AirDrop, WhatsApp, Threads, etc all appear there.
 *  2. We *also* render an explicit grid of platform deep-links so:
 *      - desktop users (no Web Share API) still have first-class sharing,
 *      - mobile users can pick a specific channel without using the OS sheet.
 *
 * Brand-tagging strategy: when no custom `text` is supplied we auto-append
 * `@camp.compassuk2026 #CampCompassUK` so every shared post promotes the
 * brand. Callers wanting a fully custom caption can pass `text` directly.
 *
 * Image-aware sharing: if `imageDataUrl` is supplied AND the browser supports
 * sharing files, the native share will include the photo too (Instagram Stories
 * users will get the image straight in the editor).
 */
export default function ShareMenu({
  url,
  title = "CampCompass UK",
  text,
  imageDataUrl = null,
  buttonClassName = "btn-secondary !px-3 !py-2 text-xs inline-flex items-center gap-1.5",
  buttonLabel = "Share",
  iconOnly = false,
  preferGrid = false,
  testId = "share-menu",
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  const fullUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  // Apply cache-buster ONLY for platforms that aggressively cache OG previews
  // (Facebook, LinkedIn, Slack). For copy-link / email / WhatsApp / SMS the
  // user expects a clean URL.
  const fullUrlBusted = withShareCacheBust(fullUrl);
  const enc = (s) => encodeURIComponent(s || "");
  // If the caller didn't pass a custom caption, build one that embeds our
  // social handle + hashtag so every shared post mentions us.
  const defaultText = `Found this on CampCompass UK 🏕️ ${SOCIAL.instagram.handle} ${SOCIAL.hashtag}`;
  const effectiveText = text || defaultText;
  const shareText = `${title} — ${effectiveText}`.trim();

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // --- File payload for native share (image + metadata) ---
  const buildShareFiles = async () => {
    if (!imageDataUrl || !imageDataUrl.startsWith("data:")) return null;
    try {
      const res = await fetch(imageDataUrl);
      const blob = await res.blob();
      const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
      const file = new File([blob], `campcompass-${Date.now()}.${ext}`, { type: blob.type });
      return [file];
    } catch { return null; }
  };

  const onTriggerClick = async () => {
    // Some callers (e.g. the Landing page "Invite a friend" CTA) want to
    // ALWAYS show the social-grid popover so users see Instagram/Facebook/X
    // explicitly rather than the OS-default share sheet (which on mobile
    // surfaces Messages first). Set preferGrid={true} to skip native share.
    if (preferGrid) {
      setOpen((v) => !v);
      return;
    }
    // Always try native share first — biggest reach (Instagram Stories,
    // AirDrop, every messaging app the user has installed).
    if (typeof navigator !== "undefined" && navigator.share) {
      const payload = { title, text: shareText, url: fullUrl };
      try {
        const files = await buildShareFiles();
        if (files && navigator.canShare && navigator.canShare({ files })) {
          payload.files = files;
        }
        await navigator.share(payload);
        return;
      } catch (e) {
        // User cancelled → just open our popover instead
        if (e && e.name === "AbortError") return;
        // Anything else (permission, type rejection) — fall through to popover
      }
    }
    setOpen((v) => !v);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success("Link copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy. Long-press the link to copy manually.");
    }
  };

  // Instagram requires special handling — they don't expose a web share URL.
  // - Mobile: invoke native share with the image file → user picks Instagram
  //   from the OS share sheet → choose Story or Feed inside the app.
  // - Desktop: copy image to clipboard if possible + open instagram.com in a
  //   new tab + show a toast telling the user to paste.
  const onInstagramClick = async (e) => {
    e.preventDefault();
    trackClick("instagram");

    // Try native share with the photo file (best mobile UX)
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        const files = await buildShareFiles();
        const payload = files
          ? { title, text: shareText, url: fullUrl, files }
          : { title, text: shareText, url: fullUrl };
        if (!files || (navigator.canShare && navigator.canShare({ files }))) {
          await navigator.share(payload);
          setOpen(false);
          return;
        }
      } catch (err) {
        if (err && err.name === "AbortError") { setOpen(false); return; }
        // fall through to clipboard fallback
      }
    }

    // Desktop / no Web Share — copy the image (if any) + open Instagram
    let copiedImage = false;
    if (imageDataUrl && imageDataUrl.startsWith("data:")) {
      try {
        const blob = await (await fetch(imageDataUrl)).blob();
        if (navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([
            new ClipboardItem({ [blob.type]: blob }),
          ]);
          copiedImage = true;
        }
      } catch {}
    }
    // Always also copy the URL to clipboard so the link is at least pasteable
    try { await navigator.clipboard.writeText(fullUrl); } catch {}

    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
    toast.success(
      copiedImage
        ? "Photo + link copied. Paste in Instagram → New Post / Story."
        : "Link copied. Open Instagram and paste it into your bio or a Story sticker.",
      { duration: 6000 }
    );
    setOpen(false);
  };

  const TARGETS = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      bg: "bg-[#25D366]",
      href: `https://wa.me/?text=${enc(`${shareText} ${fullUrl}`)}`,
      Icon: WhatsAppIcon,
    },
    {
      key: "instagram",
      label: "Instagram",
      bg: "bg-gradient-to-tr from-[#f09433] via-[#e6683c] via-50% to-[#bc1888]",
      href: "https://www.instagram.com/",
      Icon: InstagramIcon,
      onClick: onInstagramClick,
    },
    {
      key: "x",
      label: "X / Twitter",
      bg: "bg-black",
      href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(fullUrl)}`,
      Icon: XIcon,
    },
    {
      key: "facebook",
      label: "Facebook",
      bg: "bg-[#1877F2]",
      // Cache-busted URL — Facebook's OG cache lasts 24-72h, so a per-day
      // ?v= token forces a fresh preview when og:image/title were updated.
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(fullUrlBusted)}`,
      Icon: FacebookIcon,
    },
    {
      key: "threads",
      label: "Threads",
      bg: "bg-black",
      href: `https://www.threads.net/intent/post?text=${enc(`${shareText} ${fullUrl}`)}`,
      Icon: ThreadsIcon,
    },
    {
      key: "reddit",
      label: "Reddit",
      bg: "bg-[#FF4500]",
      href: `https://www.reddit.com/submit?url=${enc(fullUrl)}&title=${enc(title)}`,
      Icon: RedditIcon,
    },
    {
      key: "pinterest",
      label: "Pinterest",
      bg: "bg-[#E60023]",
      href: `https://pinterest.com/pin/create/button/?url=${enc(fullUrl)}&description=${enc(shareText)}`,
      Icon: PinterestIcon,
    },
    {
      key: "telegram",
      label: "Telegram",
      bg: "bg-[#26A5E4]",
      href: `https://t.me/share/url?url=${enc(fullUrl)}&text=${enc(shareText)}`,
      Icon: Send,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      bg: "bg-[#0A66C2]",
      // LinkedIn caches OG previews ~7d — same per-day cache-bust strategy
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(fullUrlBusted)}`,
      Icon: LinkedInIcon,
    },
    {
      key: "messenger",
      label: "Messenger",
      bg: "bg-[#0084FF]",
      // App-link works on mobile, falls back to fb.com on desktop
      href: `https://www.facebook.com/dialog/send?link=${enc(fullUrlBusted)}&app_id=140586622674265&redirect_uri=${enc(fullUrlBusted)}`,
      Icon: MessageCircle,
    },
    {
      key: "email",
      label: "Email",
      bg: "bg-[#444]",
      href: `mailto:?subject=${enc(title)}&body=${enc(`${shareText}\n\n${fullUrl}`)}`,
      Icon: Mail,
    },
  ];

  const trackClick = (key) => {
    // Lightweight analytics hook — fire-and-forget so it never blocks the open
    try {
      if (window?.posthog?.capture) {
        window.posthog.capture("share_click", { channel: key, url: fullUrl });
      }
    } catch {}
  };

  return (
    <div className="relative inline-block" data-testid={testId}>
      <button
        ref={btnRef}
        onClick={onTriggerClick}
        className={buttonClassName}
        aria-label="Share"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`${testId}-trigger`}
      >
        <Share2 className={iconOnly ? "w-4 h-4" : "w-3.5 h-3.5"} strokeWidth={2} />
        {!iconOnly && <span>{buttonLabel}</span>}
      </button>

      {open && (
        <>
          {/* Mobile backdrop — only on small screens to dim background under bottom-sheet */}
          <div
            className="sm:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            ref={popRef}
            className="fixed inset-x-3 bottom-3 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:bottom-auto sm:left-auto sm:w-[300px] z-50 bg-white border-2 hairline-strong shadow-[0_8px_0_0_#111]"
            role="menu"
            data-testid={`${testId}-popover`}
          >
            <div className="px-4 py-3 border-b hairline flex items-center justify-between">
              <span className="eyebrow">{buttonLabel === "Invite a friend" ? "Pick a place to share" : "Share trip"}</span>
              <button
                onClick={() => setOpen(false)}
                className="p-1 -m-1 hover:bg-[#f5f5f5]"
                aria-label="Close share menu"
                data-testid={`${testId}-close`}
              >
                <X className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </div>

            <div className="p-3 grid grid-cols-4 gap-2">
              {TARGETS.map(({ key, label, bg, href, Icon, onClick: customClick }) => (
                <a
                  key={key}
                  href={href}
                  target={key === "email" ? "_self" : "_blank"}
                  rel="noopener noreferrer"
                  onClick={customClick
                    ? customClick
                    : () => { trackClick(key); setTimeout(() => setOpen(false), 180); }
                  }
                  className="flex flex-col items-center gap-1.5 p-2 hover:bg-[#fff4ee] transition-colors group"
                  data-testid={`${testId}-${key}`}
                  aria-label={`Share on ${label}`}
                >
                  <span className={`w-10 h-10 ${bg} text-white inline-flex items-center justify-center group-hover:scale-105 transition-transform`}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="text-[10px] mono uppercase tracking-wider text-[#333] text-center leading-tight">{label}</span>
                </a>
              ))}
            </div>

            <div className="px-3 pb-3">
              <button
                onClick={onCopy}
                className="w-full flex items-center gap-2 px-3 py-2.5 bg-[#fafafa] border hairline hover:bg-[#fff4ee] text-xs mono"
                data-testid={`${testId}-copy`}
              >
                {copied ? (
                  <Check className="w-3.5 h-3.5 text-[#22c55e]" strokeWidth={2.5} />
                ) : (
                  <LinkIcon className="w-3.5 h-3.5 text-[#666]" strokeWidth={2} />
                )}
                <span className="flex-1 text-left truncate">{fullUrl.replace(/^https?:\/\//, "")}</span>
                <span className="text-[10px] uppercase tracking-wider font-bold">{copied ? "Copied" : "Copy"}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Brand-accurate inline SVG icons (lucide doesn't ship them)         */
/* ------------------------------------------------------------------ */
const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.881.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.881-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.982zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z"/>
  </svg>
);
const XIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);
const FacebookIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);
const ThreadsIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.5 12.011c0-3.566.85-6.42 2.495-8.486C5.845.245 8.604.024 12.179 0h.007c2.74 0 5.027.602 6.794 1.79 1.665 1.118 2.83 2.706 3.456 4.732l-2.224.704c-1.054-3.426-3.557-5.061-7.038-5.061-2.835.013-4.978.806-6.366 2.357-1.301 1.451-1.972 3.553-1.992 6.252.02 2.7.69 4.802 1.992 6.253 1.388 1.55 3.531 2.343 6.367 2.357 2.523-.014 4.18-.65 5.564-2.025 1.572-1.563 1.546-3.485.946-4.626-.354-.673-.997-1.236-1.84-1.642-.213 1.531-.74 2.768-1.575 3.69-1.024 1.13-2.405 1.749-4.038 1.812-1.232.05-2.413-.243-3.32-.819-1.061-.692-1.692-1.732-1.717-2.871-.058-2.244 1.74-3.857 4.477-4.014.967-.054 1.872.008 2.715.197-.108-.665-.327-1.193-.654-1.567-.451-.515-1.142-.776-2.057-.776-.027 0-.058 0-.085.002-.732 0-1.722.203-2.353 1.144l-1.91-1.282c.85-1.292 2.224-2.001 3.876-2.001.05 0 .102 0 .151.002 2.749.034 4.391 1.671 4.546 4.42.085.034.169.07.252.107 1.49.692 2.583 1.736 3.156 3.012.785 1.78.789 4.683-1.484 6.949-1.755 1.745-3.876 2.535-7.064 2.554zm1.024-9.804c-.55 0-1.094.022-1.622.069-1.706.097-2.643.939-2.611 2.139.044 1.273 1.426 2.067 3.064 1.99 1.524-.064 2.911-.86 3.064-3.144-.518-.242-1.218-.4-1.895-.444-.012 0-.025-.001-.037-.001a3.66 3.66 0 00-.464.029z"/>
  </svg>
);
const RedditIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.04 1.605a3.32 3.32 0 01.03.46c0 2.323-2.97 4.205-6.644 4.205-3.673 0-6.644-1.882-6.644-4.205 0-.157.01-.312.03-.461a1.755 1.755 0 01-1.04-1.604c0-.968.786-1.754 1.754-1.754.477 0 .898.182 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.111-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 .029-.463.33.33 0 00-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.232-.095z"/>
  </svg>
);
const PinterestIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);
const LinkedInIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);
const InstagramIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);
